/**
 * TableSkeleton - 表格加载骨架屏组件
 *
 * 在数据加载时显示的占位符 UI，
 * 提供更好的用户体验。
 */

import React from 'react';

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
}

const SkeletonCell: React.FC<{ width?: string }> = ({ width = 'w-full' }) => (
  <div className={`h-4 bg-gray-200 rounded animate-pulse ${width}`}></div>
);

const SkeletonRow: React.FC<{ columns: number }> = ({ columns }) => (
  <tr className="border-b border-gray-100">
    {/* Checkbox column */}
    <td className="px-3 py-3">
      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    {/* Data columns */}
    {Array.from({ length: columns }).map((_, index) => (
      <td key={index} className="px-3 py-3">
        <SkeletonCell width={index === 0 ? 'w-24' : index === 1 ? 'w-32' : 'w-20'} />
      </td>
    ))}
    {/* Actions column */}
    <td className="px-3 py-3">
      <div className="flex gap-2">
        <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </td>
  </tr>
);

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 10,
  columns = 8,
  showHeader = true,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full">
        {showHeader && (
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-3 text-left">
                <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
              </th>
              {Array.from({ length: columns }).map((_, index) => (
                <th key={index} className="px-3 py-3 text-left">
                  <div className="h-4 bg-gray-300 rounded animate-pulse w-16"></div>
                </th>
              ))}
              <th className="px-3 py-3 text-left">
                <div className="h-4 bg-gray-300 rounded animate-pulse w-12"></div>
              </th>
            </tr>
          </thead>
        )}
        <tbody>
          {Array.from({ length: rows }).map((_, index) => (
            <SkeletonRow key={index} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * StatisticsSkeleton - 统计卡片骨架屏
 */
export const StatisticsSkeleton: React.FC = () => {
  return (
    <div className="space-y-3">
      {/* Header skeleton */}
      <div className="h-6 bg-gray-200 rounded animate-pulse w-24"></div>

      {/* Stats cards skeleton */}
      <div className="space-y-2">
        {Array.from({ length: 12 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-2 bg-gray-50 rounded"
          >
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
            </div>
            <div className="h-5 bg-gray-200 rounded animate-pulse w-8"></div>
          </div>
        ))}
      </div>

      {/* Action buttons skeleton */}
      <div className="space-y-2 pt-2">
        <div className="h-9 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-9 bg-gray-200 rounded animate-pulse"></div>
      </div>
    </div>
  );
};

/**
 * FiltersSkeleton - 过滤器骨架屏
 */
export const FiltersSkeleton: React.FC = () => {
  return (
    <div className="flex flex-wrap gap-3 mb-4">
      {/* Search input skeleton */}
      <div className="w-64 h-9 bg-gray-200 rounded animate-pulse"></div>

      {/* Filter dropdowns skeleton */}
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="w-32 h-9 bg-gray-200 rounded animate-pulse"></div>
      ))}
    </div>
  );
};

